import React, { useEffect, useRef, useState } from 'react';
//import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapContainer } from './CustomMapContainer'; // Import the extended MapContainer
import VerifiedBadge from "./VerifiedBadge";

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { OpenStreetMapProvider, GeoSearchControl } from 'leaflet-geosearch';
import { FormControl, InputLabel, Select, MenuItem, OutlinedInput, Box, Checkbox, ListItemText, Button } from '@mui/material';
import '../App.css';
import CurrentLocationButton from './CurrentLocationButton';
import { fetchFeatures, fetchClientTypes, fetchNearByClients, fetchNearByClients2 } from './useApi';
import SearchIcon from '@mui/icons-material/Search';
import 'leaflet-geosearch/dist/geosearch.css';
import { BadgeVerificationStatus } from './BadgeVerificationStatus';
// import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
// import 'leaflet-gesture-handling';
//import { LeafletContext } from 'react-leaflet';

// Extend Leaflet types for gesture handling
declare global {
  namespace L {
    let GestureHandling: any;
  }
}

// Fix for default marker icon issue
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

async function loadFeatures() {
  const features = await fetchFeatures();
  // features is a Feature[]
}

async function loadClientTypess() {
  const features = await fetchClientTypes();
}

// Helper component to re-center the map
function RecenterMap({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView([lat, lng]);
  }, [map, lat, lng]);

  return null;
}

function MultiSelectDropdown({
  label,
  items,
  selected,
  setSelected,
  size = 'medium',
  sx = {},
}: {
  label: string;
  items: { id: string; name: string }[];
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  size?: 'small' | 'medium';
  sx?: object;
}) {
  const handleChange = (event: any) => {
    const newValue = event.target.value as string[];

    console.log('New value: ' + newValue.join(';') )
    console.log('Selected before change: ' + selected.join(';') )

    const allInSelected = selected.includes('0');
    const allInNewValue = newValue.includes('0');
    const newValuesWithoutAll = newValue.filter((id) => id !== '0');

    const differenceInSelected = selected.filter((id) => !newValue.includes(id));
    const differenceInNewValue = newValue.filter((id) => !selected.includes(id));

    if (differenceInNewValue.length > 0 && allInSelected && allInNewValue) {
      setSelected(newValuesWithoutAll);
    }
    else if (differenceInNewValue.length > 0 && !allInSelected && allInNewValue) {
      setSelected(['0']);
    }
    else {  
      setSelected(newValue);
    }    
  };

  const renderSelectedNames = (selectedIds: string[]) => {
    // If 'All' is selected, show only 'All'
    if (selectedIds.includes('0')) {
      const allOption = items.find(opt => opt.id === '0');
      return allOption ? allOption.name : 'All';
    }
    const selectedItems = items.filter((opt) => selectedIds.includes(opt.id));
    return selectedItems.map((opt) => opt.name).join(', ');
  };

  return (
    <FormControl
      sx={{
        minWidth: 180,
        maxWidth: 250,
        '& .MuiInputLabel-root': {
          color: 'var(--f4k-text-600)',
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: 'var(--f4k-brand-700)',
        },
        '& .MuiOutlinedInput-root': {
          color: 'var(--f4k-text-600)',
          '& fieldset': {
            borderColor: 'rgba(23, 74, 49, 0.22)',
          },
          '&:hover fieldset': {
            borderColor: 'var(--f4k-brand-600)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'var(--f4k-brand-700)',
          },
        },
        '& .MuiSelect-icon': {
          color: 'var(--f4k-brand-600)',
        },
        '& .Mui-focused .MuiSelect-icon': {
          color: 'var(--f4k-brand-700)',
        },
        ...sx,
      }}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={selected}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderSelectedNames}
        size={size}
      >
        {items.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            <Checkbox
              checked={selected.includes(item.id)}
              sx={{
                color: 'var(--f4k-text-600)',
                '&.Mui-checked': {
                  color: 'var(--f4k-brand-600)',
                },
              }}
            />
            <ListItemText primary={item.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

//Helper component to add the search control
const AddSearchControl = React.memo(function AddSearchControl({
  setSearchLocation,
}: {
  setSearchLocation: (lat: number, lng: number) => void;
}) {
  const map = useMap();

  useEffect(() => {
    const provider = new OpenStreetMapProvider();
    const searchControl = new GeoSearchControl({
      provider: provider,
      style: 'bar',
      showMarker: true,
      showPopup: true,
      autoClose: true,
      retainZoomLevel: false,
      position: 'topright',
    });

    map.addControl(searchControl);

    // Listen for the search result event
    const handleSearchResult = (result: any) => {
      const { x, y } = result.location;
      setSearchLocation(y, x);
    };
    map.on('geosearch/showlocation', handleSearchResult);

    return () => {
      map.removeControl(searchControl);
      map.off('geosearch/showlocation', handleSearchResult);
    };
  }, [map, setSearchLocation]);

  return null;
});

function FeaturesText({ features }: { features: { name: string }[] }) {
  const [expanded, setExpanded] = useState(false);
  const fullText = features.map((feature) => feature.name).join(', ');
  const maxChars = 50;
  const isLong = fullText.length > maxChars;

  if (!isLong) {
    return <span>{fullText}</span>;
  }

  if (expanded) {
    return (
      <>
        <span>{fullText}</span>
        <span
          onClick={() => setExpanded(false)}
          style={{
            marginLeft: 6,
            color: '#1565c0',
            cursor: 'pointer',
            fontWeight: 600,
            userSelect: 'none',
          }}
          title="Show less"
        >
          less
        </span>
      </>
    );
  }

  return (
    <>
      <span>{fullText.slice(0, maxChars)}</span>
      <span
        onClick={() => setExpanded(true)}
        style={{
          color: '#1565c0',
          cursor: 'pointer',
          fontWeight: 700,
          userSelect: 'none',
          letterSpacing: 1,
        }}
        title="Show more"
      >
        ...
      </span>
    </>
  );
}

// function InitializeMap({ onMapReady }: { onMapReady: (map: L.Map) => void }) {
//   const map = useMap();

//   useEffect(() => {
//     console.log('Map is ready:', map);
//     onMapReady(map); // Pass the map instance to the parent component
//   }, [map, onMapReady]);

//   return null;
// }

const MapComponentReactLeaflet = () => {
  const mapRef = useRef<L.Map | null>(null);
  const hasCalledApi = useRef(false); // Flag to track if the API call has been made


  const [currentPosition, setCurrentPosition] = useState({ lat: 0, lng: 0 });
  const [searchPosition, setSearchPosition] = useState<{ lat: number; lng: number } | null>(null);

  const [clientPoints, setClientPoints] = useState<
  {
    clientID: string;
    name: string;
    description: string | null;
    latitude: number;
    longitude: number;
    badgeTypeId: number;
    clientTypeName: string | null;
    clientFeatures: { name: string }[];
    website: string | null;
    email: string | null;
    phone: string | null;
  }[]
>([]);

  const hasValue = (value: string | null | undefined) =>
    Boolean(value && value.trim().length > 0);

// Add the viewport height adjustment here
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01; // 1% of the viewport height
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight(); // Set the initial height
    window.addEventListener('resize', setViewportHeight); // Update on resize

    return () => {
      window.removeEventListener('resize', setViewportHeight); // Cleanup on unmount
    };
  }, []);

// async function loadNearByClients(latitude: number, longitude: number, radiusKm: number) {
//   const clients = await fetchNearByClients(latitude, longitude, radiusKm);
//   // Map to only the needed fields
//   const points = clients.map((c: any) => ({
//     clientID: c.clientID,
//     name: c.name,
//     description: c.description,
//     latitude: c.latitude,
//     longitude: c.longitude,
//   }));
//   console.log('loadNearByClients: Mapped client points:', points);

// // Update state with Current Location Point
// navigator.geolocation.getCurrentPosition(
//       (position) => {
//         const newPosition = {
//           lat: position.coords.latitude,
//           lng: position.coords.longitude,
//         };
//         // Add newPosition to points
//         points.unshift({
//           clientID: 'current-location',
//           name: 'Current Location',
//           description: null,
//           latitude: newPosition.lat,
//           longitude: newPosition.lng,
//         });        
//       },
//       (error) => {
//         console.error('Error fetching current position iz CurrentLocationButton:', error);
//       },
//       { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
//     );

//     console.log('loadNearByClients: Mapped client points:', points);
//   setClientPoints(points);
// }

async function loadNearByClients2(
  southWestLat: number,
  southWestLng: number,
  northEastLat: number,
  northEastLng: number,
  clientTypeIds: string[] | null = null,
  featureIds: string[] | null = null
) {
  const clients = await fetchNearByClients2(
    southWestLat,
    southWestLng,
    northEastLat,
    northEastLng,
    clientTypeIds,
    featureIds
  );

  // Map to only the needed fields
  const points = clients.map((c: any) => ({
    clientID: c.clientID,
    name: c.name,
    description: c.description,
    latitude: c.latitude,
    longitude: c.longitude,
    badgeTypeId: c.badgeTypeId,
    clientTypeName: c.clientTypeName ?? null,
    clientFeatures: Array.isArray(c.clientFeatures) ? c.clientFeatures : [],
    website: c.website ?? null,
    email: c.email ?? null,
    phone: c.phone ?? null,
  }));

  console.log('loadNearByClients2: Mapped client points:', points);

    // Update state with Current Location Point
    // navigator.geolocation.getCurrentPosition(
    //   (position) => {
    //     const newPosition = {
    //       lat: position.coords.latitude,
    //       lng: position.coords.longitude,
    //     };
    //     // Add newPosition to points
    //     points.push({
    //       clientID: 'e4bf7ede-5fb6-46f5-b08e-dfe6b37bff48',
    //       name: 'CURRENTPOSITION',
    //       description: 'This is your current location',
    //       latitude: newPosition.lat,
    //       longitude: newPosition.lng,
    //     });        
    //     console.log('loadNearByClients2: Mapped client points + CURRENT POSITION:', points);
    
    //     setClientPoints(points);
    //   },
    //   (error) => {
    //     console.error('Error fetching current position iz CurrentLocationButton:', error);
    //   },
    //   { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    // );

    setClientPoints(points);

}

  const [selectedClientTypesOptions, setSelectedClientTypesOptions] = useState<{ id: string; name: string }[]>([
    { id: '0', name: 'All' },
  ]);
  const [selectedClientTypes, setSelectedClientTypes] = useState<string[]>(['0']);

  const [selectedFeaturesOptions, setSelectedFeaturesOptions] = useState<{ id: string; name: string }[]>([
    { id: '0', name: 'All' },
  ]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(['0']);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
                
        // Update the current position in the parent component
        setCurrentPosition(newPosition);
        },
      (error) => {
        console.error('Error fetching current position iz CurrentLocationButton:', error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }        
    );
    
    // Load features and set amenityOptions
    async function loadClientTypesAndSetFacilities() {

      const clientTypes = await fetchClientTypes();
      const mappedclientTypes = clientTypes.map((clientType: any, idx: number) => ({
        id: clientType.clientTypeID,
        name: clientType.name
      }));
      setSelectedClientTypesOptions([{ id: '0', name: 'All' }, ...mappedclientTypes]);

      const features = await fetchFeatures();
      const mappedFeatures = features.map((feature: any, idx: number) => ({
        id: feature.featureID,
        name: feature.name
      }));
      setSelectedFeaturesOptions([{ id: '0', name: 'All' }, ...mappedFeatures]);

    }
    loadClientTypesAndSetFacilities();

  }, []);

  const handleSearchLocation = (lat: number, lng: number) => {
    setSearchPosition({ lat, lng });
  };

  const redIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const blueIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

const handleApiButtonClick = async () => {
  try {
    if (!mapRef.current) {
      console.error('Map instance is not available');
      return;
    }

    // navigator.geolocation.getCurrentPosition(
    //   (position) => {
    //     const newPosition = {
    //       lat: position.coords.latitude,
    //       lng: position.coords.longitude,
    //     };

    //     console.log('Updated current position:', newPosition);

    //     // Update the current position state
    //     setCurrentPosition(newPosition);
    //   },
    //   (error) => {
    //     console.error('Geolocation error:', error);
    //     alert(`Error fetching location: ${error.message}`);
    //   },
    //   { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    // );

    const map = mapRef.current;
    const bounds = map.getBounds();

    // Check if selectedClientTypes contains only '0'
    const clientTypeIds = selectedClientTypes.length === 1 && selectedClientTypes[0] === '0'
      ? null
      : selectedClientTypes.map((id) => id.toString());

    const featuresIds = selectedFeatures.length === 1 && selectedFeatures[0] === '0'
      ? null
      : selectedFeatures.map((id) => id.toString());

    // Extract southwest and northeast corners
    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();

    // Call loadNearByClients2 with bounds and optional filters
    await loadNearByClients2(
      southWest.lat,
      southWest.lng,
      northEast.lat,
      northEast.lng,
      clientTypeIds,
      featuresIds
    );
  } catch (error) {
    if (typeof error === 'object' && error !== null && 'response' in error) {
    console.error('API error response:', (error as any).response.data);
  } else {
    console.error('API error:', error);
  }
  }


};

// useEffect(() => {
//   if (mapRef.current) {
//     console.log('Application started, calling handleApiButtonClick...');
//     handleApiButtonClick();
//   }
// }, [mapRef]);

  // useEffect(() => {
  //   console.log('AAAAA');
  //   handleApiButtonClick();
  //   if (mapRef.current) {
  //     console.log('Map is ready, calling handleApiButtonClick...');
  //     handleApiButtonClick();
  //   }
  // }, [mapRef]);

  useEffect(() => {
  if (mapRef.current && !hasCalledApi.current) {
    console.log('Map instance is available, calling handleApiButtonClick...');
    
    const map = mapRef.current;
    //setTimeout(() => {
      
      if (currentPosition.lat === 0 && currentPosition.lng === 0) {
        console.warn('Current position is not set yet');
        return;
      }
      
    //   // Check if the map is positioned to the current position
    //   const mapCenter = map.getCenter();
    //   const bounds = map.getBounds();
      
    //   const isMapCentered =
    //   Math.abs(mapCenter.lat - currentPosition.lat) < 0.001 && // Allow a small margin of error
    //   Math.abs(mapCenter.lng - currentPosition.lng) < 0.001;
      
    //   const isCurrentPositionInBounds = bounds.contains([currentPosition.lat, currentPosition.lng]);
      
    //   if (!isMapCentered) {
    //   console.warn('Map is not centered on the current position');
    //   return;
    // }

    // if (!isCurrentPositionInBounds) {
    //   console.warn('Current position is not within the map bounds');
    //   return;
    // }
    
    console.log('Map is positioned correctly, fetching bounds...');
    
    handleApiButtonClick(); // Call the API
    hasCalledApi.current = true; // Set the flag to true to prevent multiple calls
  //}, 1000);
  }
}, [mapRef, handleApiButtonClick]); // Dependencies

  // useEffect(() => {
  //   if (mapRef.current && !hasCalledApi.current) {
  //     const map = mapRef.current;
  //     const bounds = map.getBounds();

  //     // Check if the bounds are valid
  //     const southWest = bounds.getSouthWest();
  //     const northEast = bounds.getNorthEast();

  //     if (
  //       southWest.lat !== northEast.lat && // Ensure bounds are not a single point
  //       southWest.lng !== northEast.lng
  //     ) {
  //       console.log('Map bounds are valid, calling handleApiButtonClick...');
  //       handleApiButtonClick(); // Call the API
  //       hasCalledApi.current = true; // Set the flag to true to prevent multiple calls
  //     } else {
  //       console.warn('Map bounds are not valid, skipping handleApiButtonClick...');
  //     }
  //   }
  // }, [mapRef, handleApiButtonClick]); // Dependencies



 useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const onMapMoveEnd = () => {
      console.log('Map moved, calling handleApiButtonClick...');
      handleApiButtonClick();
    };

    map.on('moveend', onMapMoveEnd);

    return () => {
      map.off('moveend', onMapMoveEnd);
    };
  }, [mapRef, handleApiButtonClick]);

  // useEffect(() => {
  //   if (!mapRef.current) return;

  //   const map = mapRef.current;

  //   const handleMoveEnd = () => {
  //     console.log('Map moveend triggered, calling handleApiButtonClick...');
  //     handleApiButtonClick(); // Call the API when the bounds are accurate
  //   };

  //   map.on('moveend', handleMoveEnd); // Add the listener

  //   return () => {
  //     map.off('moveend', handleMoveEnd); // Clean up the listener on unmount
  //   };
  // }, [mapRef, handleApiButtonClick]); // Dependencies

function offsetDuplicateMarkers(points: typeof clientPoints) {
  const coordMap = new Map<string, number>();
  const offset = 0.00005; // ~5 meters

  return points.map((point) => {
    const key = `${point.latitude},${point.longitude}`;
    const count = coordMap.get(key) || 0;
    coordMap.set(key, count + 1);

    // Offset only if there are duplicates
    if (count > 0) {
      // Spread out in a circle
      const angle = (count * 45) * (Math.PI / 180); // 45deg steps
      return {
        ...point,
        latitude: point.latitude + Math.sin(angle) * offset,
        longitude: point.longitude + Math.cos(angle) * offset,
      };
    }
    return point;
  });
}

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', marginBottom: '130px' }}>
      <FormControl>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 2,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,250,248,0.92))',
            border: '1px solid rgba(23, 74, 49, 0.16)',
            boxShadow: '0 8px 20px rgba(18, 55, 36, 0.08)',
            borderRadius: 2,
            p: 2,
            mb: 2,
            // width: { xs: '100%', sm: 'auto' }, // <-- full width on mobile, auto on desktop
            maxWidth: '100vw', // <-- never overflow screen
            // alignSelf: 'flex-start',
            boxSizing: 'border-box',
          }}
        >
          <MultiSelectDropdown
            label="Facilities"
            items={selectedClientTypesOptions}
            selected={selectedClientTypes}
            setSelected={setSelectedClientTypes}
            size="small"
            sx={{ minWidth: 180, maxWidth: 250 }}
          />
          <MultiSelectDropdown
            label="Amenities"
            items={selectedFeaturesOptions}
            selected={selectedFeatures}
            setSelected={setSelectedFeatures}
            size="small"
            sx={{ minWidth: 180, maxWidth: 250 }}
          />
          <Button
            variant="contained"
            onClick={handleApiButtonClick}
            size="small"
            sx={{
              minWidth: 40,
              px: 1.5,
              backgroundColor: 'var(--f4k-brand-600)',
              color: '#fff',
              '&:hover': {
                backgroundColor: 'var(--f4k-brand-700)',
              },
            }}
          >
            <SearchIcon />
          </Button>
        </Box>
      </FormControl>

      <MapContainer
        className="map-container" 
        center={[currentPosition.lat, currentPosition.lng]}
        zoom={13}
        minZoom={11}
        scrollWheelZoom={true} // Enable scroll zoom
        //gestureHandling={true} // Enable gesture handling
        attributionControl={false} // Disable default attribution control
        dragging={true}
        ref={mapRef} // Capture map instance       
        // whenReady={() => {
        //   console.log('whenReady triggered 1');
        //   handleApiButtonClick(); // Initial load of clients
        //   if (mapRef.current) {
        //     console.log('whenReady triggered:', mapRef.current); // Debugging log
        //     // Perform any additional setup with the map instance here
        //   }
        // }}
        // whenCreated={(mapInstance) => {
        //   console.log('whenCreated triggered with map instance:', mapInstance); // Debugging log
        //   mapRef.current = mapInstance; // Set the map instance to the ref
        //   handleApiButtonClick(); // Call the API when the map is created
        // }}
        // onMapReady={(map) => {
        //   console.log('111'); // Debugging log
        //   if (!hasCalledApi.current) {
        //     console.log('Map is ready:', map);
        //     mapRef.current = map; // Set the map instance to the ref

        //     // Wait for the moveend event to ensure bounds are accurate
        //     const handleMoveEnd = () => {
        //       console.log('Map moveend triggered, calling handleApiButtonClick...');
        //       handleApiButtonClick(); // Call the API when the bounds are accurate
        //       hasCalledApi.current = true; // Set the flag to true
        //       map.off('moveend', handleMoveEnd); // Remove the listener after the first call
        //     };

        //     map.on('moveend', handleMoveEnd); // Listen for the moveend event
        //   }
        // }}
        // onMapReady={(map) => {
        //   if (!hasCalledApi.current) {
        //   console.log('Map is ready:', map);
        //   mapRef.current = map; // Set the map instance to the ref

        //   // Ensure the map is fully rendered
        //   setTimeout(() => {
        //     map.invalidateSize(); // Fix any rendering issues
        //     console.log('Map size invalidated');
        //   }, 0);

        //   // Wait for the moveend event to ensure bounds are accurate
        //   const handleMoveEnd = () => {
        //     console.log('Map moveend triggered, calling handleApiButtonClick...');
        //     handleApiButtonClick(); // Call the API when the bounds are accurate
        //     hasCalledApi.current = true
        //     map.off('moveend', handleMoveEnd); // Remove the listener after the first call
        //   };

        //   map.on('moveend', handleMoveEnd); // Listen for the moveend event
        // }
        // }}
      >
        {/* <InitializeMap
          onMapReady={(map) => {
            mapRef.current = map; // Set the map instance to the ref
            handleApiButtonClick(); // Call the API when the map is ready
          }}
        /> */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <RecenterMap lat={currentPosition.lat} lng={currentPosition.lng} />
        <AddSearchControl setSearchLocation={handleSearchLocation} />
        <Marker position={[currentPosition.lat, currentPosition.lng]} icon ={blueIcon}>
          <Popup>
            <strong>You are here {currentPosition.lat}, {currentPosition.lng}</strong>
          </Popup>
        </Marker>
        {/* {searchPosition && (
          <Marker position={[searchPosition.lat, searchPosition.lng]}>
            <Popup>
              Search Result
            </Popup>
          </Marker>
        )} */}
        

        {offsetDuplicateMarkers(clientPoints).map((point, idx) => (
          <Marker
            // key={point.clientID}
            key={idx}
            position={[point.latitude, point.longitude]}
             icon= {redIcon}
            //icon={point.name === 'CURRENTPOSITION' ? blueIcon : redIcon} // Inline condition to choose the icon

          >
            <Popup>
              <div
                style={{
                  minWidth: 230,
                  maxWidth: 280,
                  color: '#1f2937',
                  fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                  lineHeight: 1.35,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <VerifiedBadge size={20} variant={point.badgeTypeId} />
                  <strong
                    style={{
                      cursor: 'pointer',
                      color: '#0d47a1',
                      textDecoration: 'underline',
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                    onClick={() => {
                      const origin = `${currentPosition.lat},${currentPosition.lng}`;
                      const destination = `${point.latitude},${point.longitude}`;
                      const travelMode = 'driving';

                      let url = '';
                      if (/Mobi|Android/i.test(navigator.userAgent)) {
                        url = `geo:${point.latitude},${point.longitude}?q=${point.latitude},${point.longitude}`;
                      } else {
                        url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=${travelMode}`;
                      }

                      window.open(url, '_blank');
                    }}
                  >
                    {point.name}
                  </strong>
                </div>

                {point.clientTypeName && (
                  <p style={{ margin: '6px 0' }}>
                    <strong style={{ color: '#111827' }}>Client type:</strong>{' '}
                    <span style={{ color: '#374151' }}>{point.clientTypeName}</span>
                  </p>
                )}

                {point.clientFeatures.length > 0 && (
                  <p style={{ margin: '6px 0' }}>
                    <strong style={{ color: '#111827' }}>Features:</strong>{' '}
                    <span style={{ color: '#374151' }}>
                      <FeaturesText features={point.clientFeatures} />
                    </span>
                  </p>
                )}

                {hasValue(point.website) && (
                  <p style={{ margin: '6px 0' }}>
                    <strong style={{ color: '#111827' }}>Website:</strong>{' '}
                    <a
                      href={
                        /^https?:\/\//i.test(point.website!)
                          ? point.website!
                          : `https://${point.website!}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1565c0' }}
                    >
                      {point.website}
                    </a>
                  </p>
                )}

                {hasValue(point.email) && (
                  <p style={{ margin: '6px 0' }}>
                    <strong style={{ color: '#111827' }}>Email:</strong>{' '}
                    <a href={`mailto:${point.email}`} style={{ color: '#1565c0' }}>
                      {point.email}
                    </a>
                  </p>
                )}

                {hasValue(point.phone) && (
                  <p style={{ margin: '6px 0' }}>
                    <strong style={{ color: '#111827' }}>Phone:</strong>{' '}
                    <a href={`tel:${point.phone}`} style={{ color: '#1565c0' }}>
                      {point.phone}
                    </a>
                  </p>
                )}

                {point.description && (
                  <p
                    style={{
                      marginTop: 10,
                      paddingTop: 8,
                      borderTop: '1px solid #e5e7eb',
                      color: '#4b5563',
                      fontStyle: 'italic',
                    }}
                  >
                    {point.description.length > 90
                      ? `${point.description.substring(0, 90)}...`
                      : point.description}
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        
        {/* <CurrentLocationButton currentPosition={currentPosition} /> */}
        <CurrentLocationButton setCurrentPosition={setCurrentPosition} />
      </MapContainer>
      </div>
    </>
  );
};

export default MapComponentReactLeaflet;